import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Eye, Star, TrendingUp } from "lucide-react";

interface Props { ownerId: string; }
interface Insight {
  id: string;
  address: string;
  views: number;
  bookings: number;
  conversion: number;
  avg_rating: number | null;
  busiest_hour: number | null;
}

const InsightsTab = ({ ownerId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Insight[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data: spots } = await supabase
        .from("parking_spots")
        .select("id, address")
        .eq("owner_id", ownerId);
      const ids = (spots ?? []).map((s) => s.id);
      if (!ids.length) {
        setRows([]);
        setLoading(false);
        return;
      }
      const [{ data: views }, { data: bookings }] = await Promise.all([
        supabase.from("spot_views").select("parking_spot_id").in("parking_spot_id", ids),
        supabase
          .from("bookings")
          .select("id, parking_spot_id, start_time, status")
          .in("parking_spot_id", ids),
      ]);
      const bookingIds = (bookings ?? []).map((b) => b.id);
      const { data: feedback } = bookingIds.length
        ? await supabase.from("feedback").select("rating, booking_id").in("booking_id", bookingIds)
        : { data: [] as any[] };

      const bookingToSpot = new Map((bookings ?? []).map((b) => [b.id, b.parking_spot_id]));
      const ratings = new Map<string, number[]>();
      (feedback ?? []).forEach((f: any) => {
        const sid = bookingToSpot.get(f.booking_id);
        if (!sid) return;
        if (!ratings.has(sid)) ratings.set(sid, []);
        ratings.get(sid)!.push(f.rating);
      });

      const result: Insight[] = (spots ?? []).map((s) => {
        const v = (views ?? []).filter((x) => x.parking_spot_id === s.id).length;
        const sb = (bookings ?? []).filter((x) => x.parking_spot_id === s.id);
        const hours = sb.map((b) => new Date(b.start_time).getHours());
        const hourCount = new Map<number, number>();
        hours.forEach((h) => hourCount.set(h, (hourCount.get(h) ?? 0) + 1));
        const busiest = Array.from(hourCount.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
        const r = ratings.get(s.id) ?? [];
        const avg = r.length ? r.reduce((a, b) => a + b, 0) / r.length : null;
        return {
          id: s.id,
          address: s.address,
          views: v,
          bookings: sb.length,
          conversion: v ? Math.round((sb.length / v) * 100) : 0,
          avg_rating: avg,
          busiest_hour: busiest,
        };
      });
      setRows(result);
      setLoading(false);
    };
    load();
  }, [ownerId]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!rows.length)
    return (
      <Card><CardContent className="py-10 text-center text-muted-foreground">No spots yet.</CardContent></Card>
    );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {rows.map((r) => (
        <Card key={r.id}>
          <CardHeader>
            <CardTitle className="text-base truncate">{r.address}</CardTitle>
            <CardDescription>Performance overview</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-muted-foreground" /> {r.views} views</div>
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-muted-foreground" /> {r.conversion}% conv</div>
            <div className="flex items-center gap-2">📅 {r.bookings} bookings</div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" /> {r.avg_rating != null ? r.avg_rating.toFixed(1) : "—"}
            </div>
            <div className="col-span-2 text-muted-foreground">
              Busiest hour: {r.busiest_hour != null ? `${r.busiest_hour}:00` : "—"}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InsightsTab;
